import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RefreshCw, Upload, Trash2, FlaskConical, Save } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import CountdownSettingsCard from "./CountdownSettingsCard";
import ShortcutsSettingsCard from "./ShortcutsSettingsCard";
import { VisualElementControl } from "@/components/VisualElementControl";
import { SCREENS, DEFAULT_STYLES } from "@/utils/bigScreenSchema";

import { API, BACKEND_URL } from '../../utils/apiBase';

export const SettingsTab = ({
  theme, themeEdits, setThemeEdits, updateTheme,
  handleBackgroundImageChange, deleteBackgroundImage,
  simulationMode, setSimulationMode,
}) => {
  const { t } = useI18n();

  // ==================================================================
  //  Big Screen Visuals — live per-element styling
  //  display_styles is a nested dict:
  //    { <screen_id>: { <element_key>: {color,size,offset_x,offset_y,...}}}
  //  Every change pushes to the API immediately (debounced 200 ms) so
  //  the Big Screen (500 ms polling) reflects the update instantly.
  // ==================================================================
  const [displayStyles, setDisplayStyles] = useState(theme?.display_styles || {});
  useEffect(() => {
    setDisplayStyles(theme?.display_styles || {});
  }, [theme?.updated_at]);

  const pushTimeoutRef = useRef(null);
  const pushStyles = (next) => {
    setDisplayStyles(next);
    if (pushTimeoutRef.current) clearTimeout(pushTimeoutRef.current);
    pushTimeoutRef.current = setTimeout(() => {
      axios.put(`${API}/theme`, { display_styles: next }).catch(() => { /* live-push errors are non-fatal */ });
    }, 100);
  };

  const patchElement = (screenId, elementKey, patch) => {
    const scrCur = displayStyles[screenId] || {};
    const elCur = scrCur[elementKey] || (DEFAULT_STYLES[screenId] || {})[elementKey] || {};
    const merged = { ...elCur, ...patch };
    pushStyles({ ...displayStyles, [screenId]: { ...scrCur, [elementKey]: merged } });
  };

  const resetVisuals = () => {
    if (!window.confirm(t('settings.confirmResetVisuals') || 'Reset ALL Big Screen text styles to defaults?')) return;
    pushStyles({});
    toast.success(t('settings.visualsReset') || 'Big Screen visuals reset');
  };

  const saveAll = async () => {
    if (pushTimeoutRef.current) {
      clearTimeout(pushTimeoutRef.current);
      pushTimeoutRef.current = null;
      try { await axios.put(`${API}/theme`, { display_styles: displayStyles }); } catch (_e) { /* non-fatal */ }
    }
    const ok = await updateTheme({ ...(themeEdits || {}) });
    if (ok) toast.success(t('settings.saved') || 'Saved');
    else toast.error(t('common.error') || 'Error');
  };

  const onToggleSimulation = async (enabled) => {
    try {
      await setSimulationMode(enabled);
      toast.success(enabled ? t('dryRun.enabled') : t('dryRun.disabled'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('dryRun.error'));
    }
  };

  // Read helpers for the small "General" card (title + background image).
  const titleShow = themeEdits.display_title_show !== undefined
    ? themeEdits.display_title_show
    : (theme.display_title_show !== false);
  const titleAuto = themeEdits.display_title_auto !== undefined
    ? themeEdits.display_title_auto
    : (theme.display_title_auto !== false);
  const titleText = themeEdits.display_title_text !== undefined
    ? themeEdits.display_title_text
    : (theme.display_title_text || "");

  // eslint-disable-next-line react/no-unstable-nested-components
  const SaveBar = ({ position }) => {
    return (
      <div
        className={`sticky ${position === "top" ? "top-0 border-b mb-4" : "bottom-0 border-t mt-4"} z-30 -mx-2 px-3 py-2 flex items-center gap-3 justify-end bg-background/95 backdrop-blur border-border rounded-md`}
        data-testid={`settings-save-bar-${position}`}
      >
        {position === "top" && (
          <span className="text-xs text-muted-foreground mr-auto hidden md:block">
            {t('settings.liveHint') || 'Colors & sizes push live to the Big Screen. Use Save to persist other settings.'}
          </span>
        )}
        <Button variant="outline" size="sm" onClick={resetVisuals} data-testid={`reset-visuals-btn-${position}`}>
          <RefreshCw size={14} className="mr-1" /> {t('settings.resetVisuals') || 'Reset visuals'}
        </Button>
        <Button size="sm" onClick={saveAll} data-testid={`save-all-btn-${position}`}>
          <Save size={14} className="mr-1" /> {t('common.save') || 'Save'}
        </Button>
      </div>
    );
  };
  void SaveBar;

  return (
    <>
      <SaveBar position="top" />

      {/* =============================================================
          Big Screen Visuals — 5 screens as tabs
         ============================================================= */}
      <Card className="p-4 sm:p-6 mb-6" data-testid="big-screen-visuals-card">
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-foreground">
            {t('settings.bigScreenVisuals') || 'Big Screen — Visuals'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.bigScreenVisualsDesc') || 'Every text on the Big Screen: color, size and offset X/Y. Live updates.'}
          </p>
        </div>

        <Tabs defaultValue={SCREENS[0].id} className="w-full">
          <TabsList className="w-full flex-wrap h-auto gap-1 justify-start bg-muted/40 p-1">
            {SCREENS.map((s) => (
              <TabsTrigger
                key={s.id}
                value={s.id}
                className="text-xs sm:text-sm"
                data-testid={`viz-tab-${s.id}`}
              >
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {SCREENS.map((s) => (
            <TabsContent key={s.id} value={s.id} className="mt-4 space-y-3" data-testid={`viz-panel-${s.id}`}>
              <p className="text-sm text-muted-foreground italic">{s.description}</p>
              {s.elements.map((el) => {
                const schemaDefault = (DEFAULT_STYLES[s.id] || {})[el.key] || {};
                const current = ((displayStyles[s.id] || {})[el.key] || {});
                const value = { ...schemaDefault, ...current };
                return (
                  <VisualElementControl
                    key={el.key}
                    label={el.label}
                    preview={el.preview}
                    testKey={`${s.id}-${el.key}`}
                    kind={el.kind}
                    value={value}
                    onChange={(v) => patchElement(s.id, el.key, v)}
                    sizeRange={el.sizeRange ? [el.sizeRange[0], el.sizeRange[1], 0.05] : undefined}
                  />
                );
              })}
            </TabsContent>
          ))}
        </Tabs>
      </Card>

      {/* =============================================================
          General settings (kept minimal per user request)
         ============================================================= */}
      <Card className="p-6 mb-6" data-testid="general-settings-card">
        <h3 className="text-2xl font-bold text-foreground mb-4">
          {t('settings.general') || 'General'}
        </h3>

        <div className="space-y-4">
          {/* Show Title on Display */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="display-title-show"
              className="w-5 h-5 accent-primary"
              checked={titleShow}
              onChange={(e) => setThemeEdits({ ...themeEdits, display_title_show: e.target.checked })}
              data-testid="display-title-show"
            />
            <label htmlFor="display-title-show" className="text-foreground font-medium">
              {t('settings.showTitleOnDisplay') || 'Show Title on Display'}
            </label>
          </div>

          {titleShow && (
            <div className="ml-8 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="display-title-auto"
                  className="w-5 h-5 accent-primary"
                  checked={titleAuto}
                  onChange={(e) => setThemeEdits({ ...themeEdits, display_title_auto: e.target.checked })}
                  data-testid="display-title-auto"
                />
                <label htmlFor="display-title-auto" className="text-foreground">
                  {t('settings.titleAutoUseRound') || 'Auto (use round name)'}
                </label>
              </div>
              {!titleAuto && (
                <Input
                  type="text"
                  placeholder={t('settings.titleCustomPlaceholder') || 'Custom title text'}
                  value={titleText}
                  onChange={(e) => setThemeEdits({ ...themeEdits, display_title_text: e.target.value })}
                  data-testid="display-title-text"
                />
              )}
            </div>
          )}

          {/* Background Image */}
          <div className="pt-2">
            <label className="block text-foreground font-medium mb-2">
              {t('settings.backgroundImage') || 'Background Image (Display Page only)'}
            </label>
            {theme.background_image_url ? (
              <div className="flex items-center gap-4">
                <img
                  src={theme.background_image_url.startsWith('http') ? theme.background_image_url : `${BACKEND_URL}${theme.background_image_url}`}
                  alt="Background"
                  className="w-40 h-24 object-cover rounded-lg border border-border"
                  data-testid="bg-preview-thumb"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <p className="text-sm text-green-500 font-bold flex-1">✓ {t('settings.bgActive') || 'Active'}</p>
                <Button variant="destructive" onClick={deleteBackgroundImage} size="sm" data-testid="delete-bg-btn">
                  <Trash2 size={14} className="mr-1" /> {t('settings.removeBackground') || 'Remove'}
                </Button>
              </div>
            ) : (
              <label className="inline-block cursor-pointer">
                <Input
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleBackgroundImageChange}
                  className="hidden"
                  id="bg-upload"
                  data-testid="bg-upload-input"
                />
                <Button asChild>
                  <span>
                    <Upload size={14} className="mr-1" /> {t('settings.chooseImage') || 'Choose image'}
                  </span>
                </Button>
              </label>
            )}
          </div>
        </div>
      </Card>

      {/* Countdown + shortcuts (unchanged, kept as-is) */}
      <CountdownSettingsCard />
      <ShortcutsSettingsCard />

      {/* Simulation (dry-run) toggle */}
      <Card
        className="p-6 mb-6 border-2"
        style={{
          borderColor: simulationMode ? "#f97316" : "rgba(249,115,22,0.4)",
          background: simulationMode ? "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(249,115,22,0.04))" : undefined,
        }}
        data-testid="simulation-toggle-card"
      >
        <div className="flex items-center gap-3 mb-2">
          <FlaskConical className="text-orange-500" size={22} />
          <h3 className="text-xl font-bold text-foreground">{t('dryRun.title') || 'Dry-run'}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{t('dryRun.description')}</p>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-5 h-5 accent-orange-500"
            checked={simulationMode}
            onChange={(e) => onToggleSimulation(e.target.checked)}
            data-testid="simulation-toggle"
          />
          <span className="text-foreground">{simulationMode ? t('dryRun.enabled') : t('dryRun.enable')}</span>
        </label>
      </Card>

      <SaveBar position="bottom" />
    </>
  );
};
